import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useVendors } from '../context/VendorsContext';
import Layout from '../components/Layout';
import './VendorsList.css';

// Row Component for Desktop
const VendorRow = ({ vendor, index, onDeleteClick, onMoveUp, onMoveDown, isFirst, isLast, isReordering }) => {
  return (
    <tr>
      <td className="order-controls">
        <button
          onClick={() => onMoveUp(index)}
          disabled={isFirst || isReordering}
          className="order-button"
          title="Move up"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 3l-6 6h12z"/>
          </svg>
        </button>
        <button
          onClick={() => onMoveDown(index)}
          disabled={isLast || isReordering}
          className="order-button"
          title="Move down"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 13l6-6H2z"/>
          </svg>
        </button>
      </td>
      <td>{vendor.name}</td>
      <td>
        {vendor.url ? (
          <a href={vendor.url} target="_blank" rel="noopener noreferrer" className="vendr-url">
            {vendor.url}
          </a>
        ) : (
          <span className="no-url">No URL</span>
        )}
      </td>
      <td>
        {vendor.image_url ? (
          <img src={vendor.image_url} alt={vendor.name} className="vendr-thumbnail" />
        ) : (
          <span className="no-image">No image</span>
        )}
      </td>
      <td>
        <div className="action-buttons">
          <Link
            to={`/vendors/edit/${vendor.id}`}
            className="edit-button"
          >
            Edit
          </Link>
          <button
            onClick={() => onDeleteClick(vendor.id)}
            className="delete-button"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
};

// Card Component for Mobile
const VendorCard = ({ vendor, index, onDeleteClick, onMoveUp, onMoveDown, isFirst, isLast, isReordering }) => {
  return (
    <div className="vendr-card">
      <div className="vendr-card-order-controls">
        <button
          onClick={() => onMoveUp(index)}
          disabled={isFirst || isReordering}
          className="order-button"
          title="Move up"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 3l-6 6h12z"/>
          </svg>
        </button>
        <button
          onClick={() => onMoveDown(index)}
          disabled={isLast || isReordering}
          className="order-button"
          title="Move down"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 13l6-6H2z"/>
          </svg>
        </button>
      </div>
      <div className="vendr-card-header">
        {vendor.image_url ? (
          <img src={vendor.image_url} alt={vendor.name} className="vendr-card-image" />
        ) : (
          <div className="vendr-card-placeholder">No image</div>
        )}
        <h3 className="vendr-card-name">{vendor.name}</h3>
      </div>
      <div className="vendr-card-content">
        <div className="vendr-card-field">
          <strong>URL:</strong>
          {vendor.url ? (
            <a href={vendor.url} target="_blank" rel="noopener noreferrer" className="vendr-url">
              {vendor.url.length > 40 ? vendor.url.substring(0, 37) + '...' : vendor.url}
            </a>
          ) : (
            <span className="no-url">No URL</span>
          )}
        </div>
      </div>
      <div className="vendr-card-actions">
        <Link
          to={`/vendors/edit/${vendor.id}`}
          className="edit-button"
        >
          Edit
        </Link>
        <button
          onClick={() => onDeleteClick(vendor.id)}
          className="delete-button"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

const VendorsList = () => {
  const { vendors, deleteVendor, updateVendorsPriority } = useVendors();
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      await deleteVendor(deleteId);
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  const handleMoveUp = async (index) => {
    if (index === 0 || isReordering) return;
    
    setIsReordering(true);
    const reorderedVendors = [...vendors];
    const temp = reorderedVendors[index];
    reorderedVendors[index] = reorderedVendors[index - 1];
    reorderedVendors[index - 1] = temp;
    
    await updateVendorsPriority(reorderedVendors);
    setIsReordering(false);
  };

  const handleMoveDown = async (index) => {
    if (index === vendors.length - 1 || isReordering) return;
    
    setIsReordering(true);
    const reorderedVendors = [...vendors];
    const temp = reorderedVendors[index];
    reorderedVendors[index] = reorderedVendors[index + 1];
    reorderedVendors[index + 1] = temp;
    
    await updateVendorsPriority(reorderedVendors);
    setIsReordering(false);
  };

  return (
    <Layout>
      <div className="vendors-list">
        <div className="vendors-header">
          <h1>Vendrs</h1>
          <Link to="/vendors/new" className="add-button">
            + Add New Vendr
          </Link>
        </div>

        {vendors.length === 0 ? (
          <div className="empty-state">
            <p>No vendrs found. Create your first vendr!</p>
            <Link to="/vendors/new" className="add-button">
              Add Vendr
            </Link>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="vendors-table-container desktop-view">
              <table className="vendors-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Order</th>
                    <th>Name</th>
                    <th>URL</th>
                    <th>Image</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((vendor, index) => (
                    <VendorRow
                      key={vendor.id}
                      vendor={vendor}
                      index={index}
                      onDeleteClick={handleDeleteClick}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                      isFirst={index === 0}
                      isLast={index === vendors.length - 1}
                      isReordering={isReordering}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="vendors-cards-container mobile-view">
              {vendors.map((vendor, index) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  index={index}
                  onDeleteClick={handleDeleteClick}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  isFirst={index === 0}
                  isLast={index === vendors.length - 1}
                  isReordering={isReordering}
                />
              ))}
            </div>
          </>
        )}

        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={handleDeleteCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Delete Vendr?</h2>
              <p>Are you sure you want to delete this vendr? This action cannot be undone.</p>
              <div className="modal-actions">
                <button onClick={handleDeleteCancel} className="cancel-button">
                  Cancel
                </button>
                <button onClick={handleDeleteConfirm} className="confirm-delete-button">
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VendorsList;
